/* agent-participants.tsx generated by @compiled/babel-plugin v0.40.0 */
import { ax, ix } from "@compiled/react/runtime";
require("@compiled/webpack-loader/css-loader!@compiled/webpack-loader/css-loader/compiled-css.css?style=._zulpze3t%7Bgap%3Avar(--ds-space-0%2C0)%7D");
require("@compiled/webpack-loader/css-loader!@compiled/webpack-loader/css-loader/compiled-css.css?style=._1e0c116y%7Bdisplay%3Ainline-flex%7D");
require("@compiled/webpack-loader/css-loader!@compiled/webpack-loader/css-loader/compiled-css.css?style=._4cvr1h6o%7Balign-items%3Acenter%7D");
require("@compiled/webpack-loader/css-loader!@compiled/webpack-loader/css-loader/compiled-css.css?style=._1o9zidpf%7Bflex-shrink%3A0%7D");
import React, { startTransition, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { graphql, useLazyLoadQuery } from 'react-relay';
import Avatar from '@atlaskit/avatar/avatar';
import AvatarSkeleton from '@atlaskit/avatar/Skeleton';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { AvatarRelay } from '@atlassian/avatar-relay/avatar-relay';
import { useResolveAgentAri } from '@atlassian/conversation-assistant-agent/controllers/agent-ari';
import type { AgentMetadata, ConversationParticipant } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { isDefaultRovoChat, normalizeAccountIdFromIdentityAri, trimToUndefined } from '@atlassian/conversation-assistant-utils/default-agent';
import type { agentParticipants_agentByIdQuery } from './__generated__/agentParticipants_agentByIdQuery.graphql';
import type { agentParticipants_userByAccountIdQuery } from './__generated__/agentParticipants_userByAccountIdQuery.graphql';
import messages from './messages';
const MAX_VISIBLE_AGENT_AVATARS = 3;
type TextColor = React.ComponentProps<typeof Text>['color'];
type AgentFooterIdentity = {
  agentId: string;
  agentIdentityAccountId?: string;
  isDefaultRovo?: boolean;
};
// Rovo hex logo, inlined from `@atlaskit/logo/raw-icons` `rovoHexIcon`. Inlined rather than imported
// because that module bundles ~44 icon data-URIs and does not tree-shake here, which would add ~9kB
// to critical page-load routes (Jira Issue View, Confluence Edit Page). Keep this in sync with
// `rovo-switch-back-mention.ts`, which uses the same data URI for default Rovo mention rows.
const ROVO_HEX_LOGO_DATA_URI = "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none' viewBox='0 0 24 24'> <path fill='%231868db' fill-rule='evenodd' d='m13.227 16.789-7.154 4.169-3.403-1.96a2.72 2.72 0 0 1-1.362-2.355V7.366c0-.973.518-1.87 1.361-2.354l6.044-3.487-.038.114a3.6 3.6 0 0 0-.172 1.088v9.278c0 1.274.68 2.45 1.786 3.085z' clip-rule='evenodd'/> <path fill='%236a9a23' fill-rule='evenodd' d='m11.296 15.671-7.193 4.153 6.607 3.812a2.73 2.73 0 0 0 2.676.026l.048-.033a2.73 2.73 0 0 0 1.232-1.525 2.7 2.7 0 0 0 .127-.822v-3.594z' clip-rule='evenodd'/> <path fill='%23af59e1' fill-rule='evenodd' d='m21.477 5.003-4.404-2.539-5.904 4.89 2.69 1.556a3.56 3.56 0 0 1 1.785 3.086v9.277a3.6 3.6 0 0 1-.21 1.202l6.044-3.486a2.71 2.71 0 0 0 1.362-2.355V7.357c0-.97-.521-1.87-1.363-2.354' clip-rule='evenodd'/> <path fill='%23fca700' fill-rule='evenodd' d='M12.74 8.266 9.353 6.312V2.718c0-.283.044-.56.127-.821A2.73 2.73 0 0 1 10.71.372V.37a.4.4 0 0 0 .048-.033 2.73 2.73 0 0 1 2.676.026l6.499 3.75z' clip-rule='evenodd'/> </svg> ";
const NAME_SKELETON_WIDTH = '72px';
const NAME_SKELETON_HEIGHT = '12px';
const AgentNameSkeleton = (): React.JSX.Element => <Skeleton width={NAME_SKELETON_WIDTH} height={NAME_SKELETON_HEIGHT} isShimmering />;
const AgentName = ({
  name,
  textColor
}: {
  name: string;
  textColor: TextColor;
}): React.JSX.Element => <Text as="span" color={textColor} size="small" weight="regular">
		{name}
	</Text>;
const DefaultRovoAvatar = ({
  name
}: {
  name: string;
}): React.JSX.Element => <Avatar appearance="hexagon" size="xsmall" name={name} src={ROVO_HEX_LOGO_DATA_URI} />;
const styles = {
  avatarStack: "_zulpze3t _1e0c116y _4cvr1h6o _1o9zidpf"
};
const getParticipantIdentity = (participant: ConversationParticipant): AgentFooterIdentity | undefined => {
  if (participant.type !== 'AGENT') {
    return undefined;
  }
  const agentId = trimToUndefined(participant.agent?.agent_id);
  if (!agentId) {
    return undefined;
  }
  return {
    agentId,
    isDefaultRovo: isDefaultRovoChat({
      agentId
    })
  };
};
export const getParticipantAgentIdentities = (participants: readonly ConversationParticipant[] | undefined): AgentFooterIdentity[] => {
  const result: AgentFooterIdentity[] = [];

  // The backend sends the conversation agent as the first participant. The UI already
  // starts from the richer conversation.agent record, so skip the duplicate first
  // participant and append any additional agents after it.
  for (const participant of participants?.slice(1) ?? []) {
    const identity = getParticipantIdentity(participant);
    if (identity) {
      result.push(identity);
    }
  }
  return result;
};
const getConversationAgentIdentity = (agent: AgentMetadata | undefined): AgentFooterIdentity | undefined => {
  const agentId = trimToUndefined(agent?.agent_id);
  if (!agentId) {
    return undefined;
  }
  const agentIdentityAccountId = trimToUndefined(agent?.identity_account_id);
  return {
    agentId,
    ...(agentIdentityAccountId ? {
      agentIdentityAccountId
    } : {}),
    isDefaultRovo: isDefaultRovoChat({
      agentId
    })
  };
};
export const getAgentFooterIdentities = ({
  participants,
  fallbackConversationAgent
}: {
  participants: readonly ConversationParticipant[] | undefined;
  fallbackConversationAgent: AgentMetadata | undefined;
}): AgentFooterIdentity[] => {
  const conversationAgentIdentity = getConversationAgentIdentity(fallbackConversationAgent);
  const participantIdentities = getParticipantAgentIdentities(participants);
  if (participantIdentities.length === 0) {
    return conversationAgentIdentity && !conversationAgentIdentity.isDefaultRovo ? [conversationAgentIdentity] : [];
  }
  const firstParticipant = participants?.[0];
  const firstParticipantIdentity = firstParticipant ? getParticipantIdentity(firstParticipant) : undefined;
  const firstParticipantMatchesConversationAgent = Boolean(firstParticipantIdentity && conversationAgentIdentity) && firstParticipantIdentity?.agentId === conversationAgentIdentity?.agentId;
  const primaryIdentity = firstParticipantMatchesConversationAgent ? conversationAgentIdentity : conversationAgentIdentity ?? firstParticipantIdentity;
  return [primaryIdentity, ...participantIdentities].filter((identity): identity is AgentFooterIdentity => Boolean(identity));
};
const AgentMetadataFallback = ({
  showName
}: {
  showName?: boolean;
}): React.JSX.Element => <Inline as="span" alignBlock="center" space="space.050">
		<AvatarSkeleton appearance="hexagon" size="xsmall" />
		{showName ? <AgentNameSkeleton /> : null}
	</Inline>;
const HydratedAgentParticipantMetadataFromIdentityAccountId = ({
  accountId,
  showName,
  textColor
}: {
  accountId: string;
  showName?: boolean;
  textColor: TextColor;
}): React.JSX.Element | null => {
  const data = useLazyLoadQuery<agentParticipants_userByAccountIdQuery>(graphql`
			query agentParticipants_userByAccountIdQuery($accountId: ID!) {
				user(accountId: $accountId) {
					name
					# AvatarRelay owns this fragment; this query only passes the fragment ref through.
					# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
					...avatarRelay_user
				}
			}
		`, {
    accountId
  }, {
    fetchPolicy: 'store-or-network'
  });
  const user = data.user ?? null;
  const relayName = user?.name;
  return <Inline as="span" alignBlock="center" space="space.050">
			{user ? <AvatarRelay userFragment={user} size="xsmall" name={relayName} /> : null}
			{showName && relayName ? <AgentName name={relayName} textColor={textColor} /> : null}
		</Inline>;
};
const HydratedAgentParticipantMetadata = ({
  agentAri,
  showName,
  textColor
}: {
  agentAri: string | null;
  showName?: boolean;
  textColor: TextColor;
}): React.JSX.Element => {
  const data = useLazyLoadQuery<agentParticipants_agentByIdQuery>(graphql`
			query agentParticipants_agentByIdQuery($agentId: ID!) {
				agentStudio_agentById(id: $agentId) @optIn(to: "AgentStudio") {
					... on AgentStudioAssistant {
						identityAccount {
							name
							# AvatarRelay owns this fragment; this query only passes the fragment ref through.
							# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
							...avatarRelay_user
						}
					}
				}
			}
		`, {
    agentId: agentAri ?? ''
  }, {
    fetchPolicy: 'store-or-network'
  });
  const identityAccount = data.agentStudio_agentById?.identityAccount ?? null;
  const relayName = identityAccount?.name;
  return <Inline as="span" alignBlock="center" space="space.050">
			{identityAccount ? <AvatarRelay userFragment={identityAccount} size="xsmall" /> : null}
			{showName && relayName ? <AgentName name={relayName} textColor={textColor} /> : null}
		</Inline>;
};
const ResolvedAgentMetadataRelayFromAgentId = ({
  cloudId,
  identity,
  showName,
  textColor
}: {
  cloudId: string;
  identity: AgentFooterIdentity;
  showName?: boolean;
  textColor: TextColor;
}): React.JSX.Element => {
  const {
    agentAri
  } = useResolveAgentAri({
    agentId: identity.agentId,
    cloudId
  });
  if (!agentAri) {
    return <AgentMetadataFallback showName={showName} />;
  }
  return <React.Suspense fallback={<AgentMetadataFallback showName={showName} />}>
			<HydratedAgentParticipantMetadata agentAri={agentAri} showName={showName} textColor={textColor} />
		</React.Suspense>;
};
const AgentMetadataRelayFromIdentity = (props: {
  cloudId: string;
  identity: AgentFooterIdentity;
  showName?: boolean;
  textColor: TextColor;
  rovoAgentName: string;
}): React.JSX.Element => {
  if (props.identity.isDefaultRovo) {
    return <Inline as="span" alignBlock="center" space="space.050">
				<DefaultRovoAvatar name={props.rovoAgentName} />
				{props.showName ? <AgentName name={props.rovoAgentName} textColor={props.textColor} /> : null}
			</Inline>;
  }
  const accountId = normalizeAccountIdFromIdentityAri(props.identity.agentIdentityAccountId);
  if (accountId) {
    return <React.Suspense fallback={<AgentMetadataFallback showName={props.showName} />}>
				<HydratedAgentParticipantMetadataFromIdentityAccountId accountId={accountId} showName={props.showName} textColor={props.textColor} />
			</React.Suspense>;
  }
  return <AgentMetadataRelayFromAgentId {...props} />;
};
const AgentMetadataRelayFromAgentId = (props: {
  cloudId: string;
  identity: AgentFooterIdentity;
  showName?: boolean;
  textColor: TextColor;
}): React.JSX.Element => {
  const [shouldResolveAgentAri, setShouldResolveAgentAri] = useState(false);
  useEffect(() => {
    setShouldResolveAgentAri(false);
    startTransition(() => {
      setShouldResolveAgentAri(true);
    });
  }, [props.identity.agentId]);
  if (!shouldResolveAgentAri) {
    return <AgentMetadataFallback showName={props.showName} />;
  }
  return <ResolvedAgentMetadataRelayFromAgentId {...props} />;
};
export const AgentParticipants = ({
  identities,
  cloudId,
  textColor
}: {
  identities: readonly AgentFooterIdentity[];
  cloudId: string | undefined;
  textColor: TextColor;
}): React.JSX.Element | null => {
  const {
    formatMessage
  } = useIntl();
  if (!cloudId || identities.length === 0) {
    return null;
  }
  if (identities.length === 1) {
    return <AgentMetadataRelayFromIdentity identity={identities[0]} cloudId={cloudId} showName textColor={textColor} rovoAgentName={formatMessage(messages.rovoAgentName)} />;
  }
  const visibleIdentities = identities.slice(0, MAX_VISIBLE_AGENT_AVATARS);
  const overflowCount = identities.length - visibleIdentities.length;
  return <Inline alignBlock="center" space="space.050">
			<Box as="span" xcss={styles.avatarStack}>
				{visibleIdentities.map(identity => <AgentMetadataRelayFromIdentity key={identity.agentId} identity={identity} cloudId={cloudId} textColor={textColor} rovoAgentName={formatMessage(messages.rovoAgentName)} />)}
			</Box>
			{overflowCount > 0 && <Box as="span" aria-label={formatMessage(messages.agentOverflowCount, {
      count: overflowCount
    })}>
					<Text size="small" weight="regular" color={textColor}>
						+{overflowCount}
					</Text>
				</Box>}
		</Inline>;
};